
import AddressDropdown from "./AddressDropdown.vue";
import _ from "lodash";
import Vue, { PropType } from "vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import avsCheck, { IChangedFlags } from "~/assets/utils/Avs/avsCheck";
import checkIfDetailUpdated from "~/assets/utils/checkIfDetailUpdated";
import highlightsDiff from "~/assets/utils/highlightsDiff";
import updateDetails from "~/assets/utils/updateDetails";
import { AVSStatus, AVSSubStatus } from "~/entities/AVS";
import { Details } from "~/entities/Details";
import Place from "~/entities/Place";
import { Country } from "~/entities/Response/FormData";
import Trip from "~/entities/Trip";

interface StageDetailsI {
  from: Details;
  to: Details;
}

export default Vue.extend({
  components: { AddressDropdown },
  props: {
    validated: { type: Boolean, default: false },
    googleObj: {
      type: Object as PropType<google.maps.places.AutocompleteOptions>,
      default: null,
    },
    allowedCountries: {
      type: Array as PropType<Country[]>,
      default: () => [],
    },
    states: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    preSelectionTrip: {
      type: Object as PropType<Trip>,
      default: () => new Trip(),
    },
    index: {
      type: Number,
      default: 0,
    },
    options: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    outShip: {
      type: Boolean,
      default: false,
    },
    userAddresses: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    validationErrors: {
      type: Array as () => Array<unknown>,
      default: () => [],
    },
  },
  data() {
    return {
      trip: new Trip(),
      equalAddress: false,
      errorFrom: false, // for future new errors
      errorTo: false, // for future new errors
      warningFrom: false, // for future new errors
      warningTo: false, // for future new errors
      toInternational: false,
      fromInternational: false,
      toCountry: "",
      fromCountry: "",
      isOpen: false as boolean,
      avsChecked: false as boolean,
      stageDetails: [] as StageDetailsI[],
      updatedDetails: [] as StageDetailsI[],
      needsAvsModal: false as boolean,
    };
  },
  computed: {
    ...mapGetters({
      details: "ship/details",
      hasAdvancedInStep: "ship/hasAdvancedInStep",
      trips: "ship/trips",
    }),
  },
  watch: {
    details: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (newVal[this.index]) {
          const from = newVal[this.index]?.from;
          const to = newVal[this.index]?.to;
          if (
            from?.avsChecked &&
            !from?.passedAvs &&
            from?.searchType?.toLowerCase() !== "partial"
          ) {
            this.warningFrom = true;
            this.requestAlert({ name: "warningAVSFailed", type: "address" });
          } else {
            this.warningFrom = false;
          }
          if (
            to?.avsChecked &&
            !to?.passedAvs &&
            to?.searchType?.toLowerCase() !== "partial"
          ) {
            this.warningTo = true;
            this.requestAlert({ name: "warningAVSFailed", type: "address" });
          } else {
            this.warningTo = false;
          }
        }
      },
    },
  },
  mounted() {
    if ((this as any).preSelectionTrip) {
      (this as any).trip = new Trip((this as any).preSelectionTrip);
    } else {
      (this as any).trip = new Trip();
    }
    if (this.details[this.index]) {
      const from = this.details[this.index]?.from;
      const to = this.details[this.index]?.to;
      if (
        from?.avsChecked &&
        !from?.passedAvs &&
        from?.searchType?.toLowerCase() !== "partial"
      ) {
        this.warningFrom = true;
      } else {
        this.warningFrom = false;
      }
      if (
        to?.avsChecked &&
        !to?.passedAvs &&
        to?.searchType?.toLowerCase() !== "partial"
      ) {
        this.warningTo = true;
      } else {
        this.warningTo = false;
      }
    }
  },
  methods: {
    // Vuex actions
    ...mapMutations({
      pushAlert: "errorsBag/addAlert",
      clearAlert: "errorsBag/clearAlert",
      setTrip: "ship/setTrip",
      setDetail: "ship/setDetail",
      clearDetail: "ship/clearDetail",
      setHasAdvancedInStep: "ship/setHasAdvancedInStep",
    }),
    ...mapActions({
      requestAlert: "errorsBag/requestAlert",
    }),
    // get country info by filtering the address_components property
    getTripCountry(place: google.maps.places.PlaceResult) {
      // if null, no country information returned from places
      return place.address_components?.find(function (el) {
        return el.types.includes("country");
      });
    },
    getCountryFromPlace(place: google.maps.places.PlaceResult) {
      let country;
      // If address and address_components then filter address_components by types to get country
      if (place && place.address_components) {
        country = place.address_components.filter(function (el) {
          return el.types.includes("country");
        });
        // if null, no country information from places
        return country.length ? country[0].long_name : null;
      }
    },
    // check if the selected place is valid, direction as parameter to avoid duplicated code
    isValidAddress(
      Address: google.maps.places.PlaceResult | any,
      direction: String
    ) {
      (this as any).trip[direction as string] = Address;
      const country = this.getTripCountry(Address);
      const isCountryAllowed = (this.allowedCountries ?? []).filter(
        (c: Country) => c.code === country?.short_name && c.active
      );
      this.$emit("countryChanged", {
        shortName: country?.short_name,
        index: this.index ? this.index : 0,
      });
      if (
        !isCountryAllowed.length &&
        typeof country !== "undefined" &&
        country?.long_name
      ) {
        this.$emit("emitError", { direction, country: country?.long_name }); // <- leave this to step or remove
        if (direction === "to") {
          this.errorTo = true;
          this.toCountry = country.short_name;
        } else {
          this.errorFrom = true;
          this.fromCountry = country.short_name;
        }
        return;
      } else {
        if (direction === "to") {
          this.errorTo = false;
          this.toCountry =
            country && country.short_name ? country.short_name : "";
        } else {
          this.errorFrom = false;
          this.fromCountry =
            country && country.short_name ? country.short_name : "";
        }
        if (this.toCountry === "AU" && this.toCountry === this.fromCountry) {
          this.errorTo = true;
          this.errorFrom = true;
          this.$emit("emitError", {
            direction,
            country: "within " + country?.long_name,
          });
        } else this.$emit("errorClean", direction); // <- leave this to step
      }
      return Address;
    },
    assignLocation(address: Place, direction: string) {
      // if the Address is valid then assign to trips.
      this.setTrip({
        direction,
        value: this.isValidAddress(address, direction),
        index: this.index ? this.index : 0,
      });
      this.checkAddressForBusiness(address, direction);
      this.checkIfInternational(address, direction);
      this.checkIfEqual();
    },
    assignUserDetails(
      {
        stageDetails,
        updatedDetails,
        changedFlags,
      }: {
        stageDetails: Details;
        updatedDetails: Details;
        changedFlags: IChangedFlags;
      },
      direction: string
    ) {
      const storePostalCode =
        this.details?.[this.index]?.[direction].postalCode;

      const inputPostalCode = stageDetails.postalCode;
      if (storePostalCode?.includes(inputPostalCode)) {
        stageDetails.postalCode = storePostalCode;
      }

      if (stageDetails) {
        if (
          !stageDetails.passedAvs &&
          !updatedDetails.passedAvs &&
          stageDetails.searchType?.toLowerCase() === "full"
        ) {
          if (direction === "from") {
            this.warningFrom = true;
          } else {
            this.warningTo = true;
          }
          this.requestAlert({ name: "warningAVSFailed", type: "address" });
        } else if (direction === "from") {
          this.warningFrom = false;
        } else {
          this.warningTo = false;
        }
        
        this.setDetail({
          direction,
          value: "clear",
          index: this.index ? this.index : 0,
        });
        this.setDetail({
          direction,
          value: stageDetails,
          index: this.index ? this.index : 0,
        });
        this.$emit("emitDetailsUpdated", {
          updatedDetails,
          direction,
          changedFlags,
        });
      } else {
        this.clearDetail({ direction, index: this.index ? this.index : 0 });
      }
      if (changedFlags) {
        this.$emit("emitDetailsUpdated", {
          updatedDetails,
          direction,
          changedFlags,
        });
      }
      this.checkIfEqual();
    },
    checkIfEqual() {
      if (this.trip) this.equalAddress = this.trip.isSameLocation();
    },
    checkAddressForBusiness(address: Place, direction: string) {
      let formattedAddress = address.originalFormattedAddress
        ? address.originalFormattedAddress
        : (address.formatted_address as string);
      if (formattedAddress) {
        formattedAddress = formattedAddress.toLowerCase();
      } else {
        formattedAddress = "";
      }
      const addressComponent = address.address_components;
      const components = {} as any;
      let company = "";
      if (
        addressComponent &&
        addressComponent.constructor === Array &&
        addressComponent.length
      ) {
        for (let i = 0; i < addressComponent.length; i++) {
          const component = addressComponent[i];
          const type = component.types[0];
          switch (type) {
            case "street_number":
              components.streetNumber = component.long_name;
              break;
            case "route":
              components.streetAddress =
                component.long_name +
                " " +
                (components.streetNumber &&
                !component.long_name.includes(components.streetNumber)
                  ? components.streetNumber
                  : "");
              components.address1 = component.short_name;
              break;
            case "postal_code":
              components.postalCode = component.long_name;
              break;
          }
        }
      }
      if (address && (address as any).company) {
        company = (address as any).company;
      } else {
        let name = address.name as string;
        if (name) {
          name = name.toLowerCase();
        } else {
          name = "";
        }
        if (
          typeof name !== "undefined" &&
          formattedAddress.includes(name) &&
          name !== components.postalCode
        ) {
          company = name;
        }
      }
      let streetName = components.address1;
      if (!streetName) {
        streetName = "";
      }
      if (
        streetName === company ||
        this.nameExists(
          company.toLowerCase(),
          addressComponent as google.maps.GeocoderAddressComponent[]
        )
      ) {
        company = "";
      }
      if (
        company &&
        !address.originalFormattedAddress
          ?.toLowerCase()
          ?.includes(company.toLowerCase())
      ) {
        const cloneDetails = _.cloneDeep(this.details);
        const details = cloneDetails?.[this.index]?.[direction]
          ? new Details(cloneDetails[this.index][direction])
          : new Details();
        details.company = company;
        this.setDetail({
          direction,
          value: details,
          index: this.index ? this.index : 0,
        });
      }
    },
    nameExists(
      name: string,
      addressComponents: google.maps.GeocoderAddressComponent[]
    ): boolean {
      if (name && name !== "" && addressComponents?.length) {
        return addressComponents.some(
          (component) =>
            component?.long_name?.toLowerCase().includes(name.toLowerCase()) ||
            component?.short_name?.toLowerCase().includes(name)
        );
      }
      return false;
    },
    checkIfInternational(
      address: google.maps.places.PlaceResult,
      direction: string
    ) {
      const addressComponent = address.address_components;
      let country = "";
      if (
        addressComponent &&
        addressComponent.constructor === Array &&
        addressComponent.length
      ) {
        for (let i = 0; i < addressComponent.length; i++) {
          const component = addressComponent[i];
          const type = component.types[0];
          switch (type) {
            case "country":
              country = component.short_name;
              break;
          }
        }
      }
      if (country && country !== "US") {
        if (direction === "to") {
          this.toInternational = true;
        } else {
          this.fromInternational = true;
        }
      } else if (direction === "to") {
        this.toInternational = false;
      } else {
        this.fromInternational = false;
      }
    },
    checkTrip(event) {
      const { id, direction } = event;
      // TODO: check if we can improve all proccess for that
      if (!this.trips?.[id]?.[direction]) {
        // this.setHasAdvancedInStep(true);
      }
    },
    async editDetailAddress(event) {
      const { id, direction, element, value } = event;
      const detailsEdited = this.details.length
        ? _.cloneDeep(this.details)
        : [{ from: new Details(), to: new Details() }];
      if (!detailsEdited[id]?.[direction]) {
        return;
      }
      detailsEdited[id][direction][element] = value;
      if (element === "address1") {
        detailsEdited[id][direction].streetAddress = value;
      }
      if (element !== "address2") {
        detailsEdited[id][direction].avsChecked = false;
        detailsEdited[id][direction].passedAvs = false;
      }
      if (this.trips[id]?.[direction]) {
        const trip = _.cloneDeep(this.trips[id][direction]);
        trip.address_components =
          detailsEdited[id][direction].mapComponentsToAddress();
        this.setTrip({ direction, value: trip, index: id });
      }
      const { stageDetails, updatedDetails, changedFlags } = await avsCheck(
        detailsEdited[id][direction]
      );
      if (stageDetails) {
        this.setDetail({ direction, value: stageDetails, index: id });
      }
      if (changedFlags) {
        this.$emit("emitDetailsUpdated", {
          updatedDetails,
          direction,
          changedFlags,
        });
      }
    },
    clearInputAddress(direction: string) {
      this.setDetail({
        direction,
        value: "clear",
        index: this.index ? this.index : 0,
      });
      this.setTrip({
        direction,
        value: new Trip(),
        index: this.index ? this.index : 0,
      });
    },
    emitLoading(event) {
      this.$emit("emitLoading", event);
    },
    // imported functions (only for html use, if not needed use in html then remove)
    highlightsDiff,
    updateDetails,
    checkIfDetailUpdated,
  },
});
